import { captureApiError, measureApiCall } from '../utils/sentry';

/**
 * Wrapper para integrar Sentry en servicios de API
 * Centraliza el monitoreo de rendimiento y captura de errores
 */

export interface ApiCallOptions {
  endpoint: string;
  method: string;
  headers?: HeadersInit;
  body?: any;
  timeout?: number;
}

/**
 * Wrapper genérico para llamadas HTTP con monitoreo Sentry integrado
 */
export const sentryApiCall = async <T>(
  baseUrl: string,
  options: ApiCallOptions,
  apiCallFn: () => Promise<T>
): Promise<T> => {
  const { endpoint, method } = options;

  try {
    return await measureApiCall(
      endpoint,
      method,
      apiCallFn
    );
  } catch (error) {
    captureApiError(error, endpoint, method, {
      baseUrl,
      timeout: options.timeout,
      hasBody: !!options.body,
      headersCount: options.headers ? Object.keys(options.headers).length : 0
    });

    throw error;
  }
};

/**
 * Función helper para crear headers comunes
 */
export const createApiHeaders = (
  contentType: string = 'application/json',
  additionalHeaders?: Record<string, string>
): Headers => {
  const headers = new Headers();
  headers.append('Content-Type', contentType);

  if (additionalHeaders) {
    Object.entries(additionalHeaders).forEach(([key, value]) => {
      headers.append(key, value);
    });
  }

  return headers;
};

/**
 * Wrapper específico para fetch con Sentry integrado
 */
export const sentryFetch = async (
  url: string,
  options: RequestInit,
  context?: Record<string, any>
): Promise<Response> => {
  const endpoint = url.replace(new RegExp('^https?://[^/]+'), ''); // Remove base URL
  const method = options.method || 'GET';

  try {
    return await measureApiCall(
      endpoint,
      method,
      () => fetch(url, options)
    );
  } catch (error) {
    captureApiError(error, endpoint, method, {
      ...context,
      url,
      hasBody: !!options.body,
      headersCount: options.headers ? Object.keys(options.headers).length : 0
    });

    throw error;
  }
};

const sentryApiWrapper = {
  sentryApiCall,
  sentryFetch,
  createApiHeaders
};

export default sentryApiWrapper;
